import { useEffect, useState, MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import LockIcon from '@mui/icons-material/Lock';
import ResultsIcon from '@mui/icons-material/Leaderboard';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LogoutIcon from '@mui/icons-material/Logout';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import usePartnerAuth from 'hooks/usePartnerAuth';
import PartnerLoginModal from 'views/pages/partner/PartnerLoginModal';
import ChangePasswordModal from 'views/pages/partner/ChangePasswordModal';
import TerminalBalance from 'views/pages/partner/TerminalBalance';
import BottomNav from './BottomNav';
import PartnerLoginPage from 'views/pages/partner/PartnerLoginPage';
import peryaplayLogo from 'assets/images/perya-play-logo.png';

// When true, unauthenticated users see the full-page login first instead of the
// home/landing page. Toggle via VITE_APP_REQUIRE_LOGIN (default: true).
const REQUIRE_LOGIN = String(import.meta.env.VITE_APP_REQUIRE_LOGIN ?? 'true').toLowerCase() !== 'false';

const PartnerLayout = () => {
    const { mpId } = useParams();
    const navigate = useNavigate();
    const { isLoggedIn, user, enclosureName, enclosureStatus, enclosureId, logout, mismatchError, clearMismatchError, setMismatchError } =
        usePartnerAuth();
    const [loginOpen, setLoginOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [changePwOpen, setChangePwOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useDispatch();
    useEffect(() => {
        const open = () => setLoginOpen(true);
        window.addEventListener('partner:open-login', open);
        return () => window.removeEventListener('partner:open-login', open);
    }, []);

    const openMenu = (e: MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget);
    const closeMenu = () => setMenuAnchor(null);
    const handleLogout = () => {
        closeMenu();
        logout();
    };


    const handleTransaction = (modules: any) => {
        if (modules === 'tickets') {
            navigate(`/mp/${mpId}/transactions`);
        } else {
            navigate(`/mp/${mpId}/winnings`);
        }
    };

    const handleOpenChangePw = () => {
        closeMenu();
        setChangePwOpen(true);
    };

    const handleOpenRaceresults = () => {
        navigate(`/mp/${mpId}/raceresults`);
    };

    const handleCreateSSO = () => {
        navigate(`/mp/${mpId}/create-sso`);
    };

    useEffect(() => {
        const message = localStorage.getItem('mp_mpidMessage');

        if (message) {
            setMismatchError(message);
            localStorage.removeItem('mp_mpidMessage');
        }
    }, [dispatch]);

    useEffect(() => {
        const interval = setInterval(() => {
            const lastActivity = localStorage.getItem('mp_lastBetActivity');

            if (!lastActivity) return;

            const diff = Date.now() - Number(lastActivity);

            // 5 minutes = 300000 ms
            if (diff >= 5 * 60 * 1000) {
                localStorage.removeItem('mp_lastBetActivity');
                setMismatchError('For security reasons, your session has expired due to 5 minutes of inactivity. Please log in again.');
                handleLogout(); // your existing logout function
            }
        }, 60000); // check every minute

        return () => clearInterval(interval);
    }, []);

    const userInitial = (user?.username || user?.displayName || '?').toString().charAt(0).toUpperCase();

    // Full-page login gate: once the enclosure resolves, unauthenticated users
    // get the login page (no header / bottom nav) when REQUIRE_LOGIN is on.
    if (REQUIRE_LOGIN && enclosureStatus === 'valid' && !isLoggedIn) {
        return (
            <>
                <PartnerLoginPage />
                <Snackbar
                    open={!!mismatchError}
                    autoHideDuration={8000}
                    onClose={clearMismatchError}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert severity="error" variant="filled" onClose={clearMismatchError}>
                        {mismatchError}
                    </Alert>
                </Snackbar>
            </>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#121212' }}>
            <AppBar
                position="sticky"
                elevation={0}
                color="inherit"
                sx={{
                    boxShadow: '0px 0px .625rem 0px rgba(0,0,0,0.75)',
                    backdropFilter: 'blur(6px)',
                    background: '#ffffff05'
                }}
            >
                <Container>
                    <Toolbar
                        disableGutters={isMobile}
                        sx={{
                            justifyContent: 'space-between',
                            gap: { xs: 1, sm: 2 },
                            minHeight: { xs: 80, sm: 100 },
                            px: { xs: 1, sm: 2 }
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                            {/* <Box
                                component="img"
                                src={peryaplayLogo}
                                alt="PeryaPlay"
                                onClick={() => mpId && navigate(`/mp/${mpId}`)}
                                sx={{
                                    height: { xs: 56, sm: 90 },
                                    display: 'block',
                                    flexShrink: 0,
                                    cursor: mpId ? 'pointer' : 'default'
                                }}
                            /> */}
                            {/* {mpId && (
                                <Box sx={{ display: 'flex', alignItems: 'center', borderRadius: '6px', overflow: 'hidden' }}>
                                    {enclosureName && (
                                        <Box sx={{ backgroundColor: theme.palette.secondary.main, px: 1.5, py: 0.5 }}>
                                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                                {enclosureName}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box sx={{ backgroundColor: 'rgba(255,255,255,0.12)', px: 1.5, py: 0.5 }}>
                                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                            {mpId}
                                        </Typography>
                                    </Box>
                                </Box>
                            )} */}
                        </Stack>

                        {isLoggedIn ? (
                            <Stack direction="row" spacing={{ xs: 0.75, sm: 1.5 }} alignItems="center">
                                {/* Cash-in-hand pill (icon + amount) shown before the username */}
                                <TerminalBalance />
                                <Button
                                    onClick={openMenu}
                                    size="small"
                                    color="inherit"
                                    sx={{
                                        color: '#fff',
                                        textTransform: 'none',
                                        minWidth: 0,
                                        px: { xs: 0.5, sm: 1 }
                                    }}
                                    endIcon={<ArrowDropDownIcon />}
                                    startIcon={
                                        <Avatar
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                fontSize: '0.75rem',
                                                bgcolor: 'warning.main',
                                                color: '#000'
                                            }}
                                        >
                                            {userInitial}
                                        </Avatar>
                                    }
                                >
                                    {!isMobile && (
                                        <Typography
                                            component="span"
                                            sx={{
                                                color: '#fff',
                                                fontSize: '0.85rem',
                                                maxWidth: 140,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {user?.username}
                                        </Typography>
                                    )}
                                </Button>
                                <Menu
                                    anchorEl={menuAnchor}
                                    open={Boolean(menuAnchor)}
                                    onClose={closeMenu}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    slotProps={{
                                        paper: { sx: { minWidth: 200, mt: 0.5 } }
                                    }}
                                >
                                    <MenuItem disabled sx={{ opacity: '1 !important' }}>
                                        <Stack spacing={0.25}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {user?.username}
                                            </Typography>
                                            {user?.displayName && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {user.displayName}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </MenuItem>
                                    {!isMobile && (
                                        <>
                                            <Divider />
                                            <MenuItem onClick={() => handleTransaction('tickets')}>
                                                <ReceiptLongIcon fontSize="small" sx={{ mr: 1 }} />
                                                Sales
                                            </MenuItem>
                                            <Divider />
                                            <MenuItem onClick={() => handleTransaction('payouts')}>
                                                <ReceiptLongIcon fontSize="small" sx={{ mr: 1 }} />
                                                Winnings
                                            </MenuItem>
                                        </>
                                    )}
                                    <Divider />
                                    <MenuItem onClick={handleCreateSSO}>
                                        <LockIcon fontSize="small" sx={{ mr: 1 }} />
                                        Create SSO
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem onClick={handleOpenRaceresults}>
                                        <ResultsIcon fontSize="small" sx={{ mr: 1 }} />
                                        Race Results
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem onClick={handleOpenChangePw}>
                                        <LockIcon fontSize="small" sx={{ mr: 1 }} />
                                        Change Password
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem onClick={handleLogout}>
                                        <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                                        Logout
                                    </MenuItem>
                                </Menu>
                            </Stack>
                        ) : isMobile ? (
                            <IconButton
                                size="small"
                                color="warning"
                                onClick={() => setLoginOpen(true)}
                                sx={{ border: '1px solid', borderColor: 'warning.main' }}
                            >
                                <Typography sx={{ fontSize: '0.75rem', px: 0.5, fontWeight: 700 }}>Login</Typography>
                            </IconButton>
                        ) : (
                            <Button
                                size="large"
                                variant="outlined"
                                onClick={() => setLoginOpen(true)}
                                sx={{
                                    color: '#fff',
                                    borderColor: theme.palette.secondary.main,
                                    backgroundColor: 'transparent',
                                    fontWeight: 700,
                                    fontSize: '1.1rem',
                                    px: 4,
                                    py: 1.2,
                                    borderRadius: '12px',
                                    '&:hover': {
                                        backgroundColor: theme.palette.secondary.main,
                                        borderColor: theme.palette.secondary.main,
                                        color: '#fff'
                                    }
                                }}
                            >
                                Login
                            </Button>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            <Container sx={{ flex: 1, pt: 1, pb: 'calc(72px + env(safe-area-inset-bottom))' }}>
                {enclosureStatus === 'valid' ? (
                    <Outlet />
                ) : enclosureStatus === 'invalid' ? (
                    <Alert severity="error" variant="outlined">
                        {mismatchError || `Invalid marketing partner "${mpId}". This enclosure does not exist.`}
                    </Alert>
                ) : (
                    <Typography variant="body2" sx={{ color: '#ccc' }}>
                        Loading…
                    </Typography>
                )}
            </Container>

            <BottomNav />

            <PartnerLoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />

            <ChangePasswordModal open={changePwOpen} onClose={() => setChangePwOpen(false)} />

            <Snackbar
                open={!!mismatchError}
                autoHideDuration={8000}
                onClose={clearMismatchError}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="error" variant="filled" onClose={clearMismatchError}>
                    {mismatchError}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PartnerLayout;
